/**
 * Pure helpers for the multiplex MessageArea kept-alive state machine.
 *
 * Extracted to a sibling file (rather than living inside
 * `use-multiplex-mounted-ids.ts`) so the unit-test can import them
 * without dragging the React import into the test environment.
 */

// Set to 1 to collapse to the single-mount Slack-shape path: one persistent
// MessageArea fiber whose `channelId` prop swaps on each switch — refs
// (scrollAnchorByChannel, composer drafts) persist naturally across
// switches. See MultiplexLayer.tsx for the cap===1 branch.
// Bump back to 3–5 to re-enable Linear-style instant kept-alive switches.
export const MULTIPLEX_CAP: number = 1;

/**
 * Compute the next mountedIds array after a workspace-scope eviction.
 *
 * Rule: an entry survives if
 *   (1) it's the active selectedChannel (its render is imminent), OR
 *   (2) its workspace is null (DM, workspace-universal), OR
 *   (3) its workspace matches `selectedWorkspace`.
 *
 * Channels not yet in `channelWorkspaceMap` (just-joined; query hasn't
 * caught up) are treated as DMs (kept). Self-corrects once the query
 * resolves.
 */
export function filterMountedByScope(
  prev: ReadonlyArray<string>,
  selectedChannel: string | null,
  channelWorkspaceMap: ReadonlyMap<string, string | null>,
  selectedWorkspace: string | null,
): string[] {
  return prev.filter((id) => {
    if (id === selectedChannel) return true;
    const ws = channelWorkspaceMap.get(id) ?? null;
    return ws === null || ws === selectedWorkspace;
  });
}

/**
 * Compute the next mountedIds array after appending a newly-active
 * channel.
 *
 * Rule:
 *   - Append (NOT prepend) so existing slot positions are preserved.
 *   - If cap is exceeded, evict the LRU id — the mountedIds member
 *     ranked LAST in mruOrder. Never evict the active channel.
 */
export function appendWithLruEviction(
  current: ReadonlyArray<string>,
  selectedChannel: string,
  mruOrder: ReadonlyArray<string>,
  cap: number,
): string[] {
  let next = [...current, selectedChannel];
  if (next.length > cap) {
    const members = new Set(next);
    let lruId: string | null = null;
    for (let i = mruOrder.length - 1; i >= 0; i--) {
      if (members.has(mruOrder[i]) && mruOrder[i] !== selectedChannel) {
        lruId = mruOrder[i];
        break;
      }
    }
    if (lruId !== null) {
      next = next.filter((id) => id !== lruId);
    } else {
      // Fallback — should be unreachable, but preserve a sane bound.
      next = next.slice(next.length - cap);
    }
  }
  return next;
}
